import { initializeApp, getApps } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import firebaseConfig, { firebaseVapidKey } from "configs/firebase.config";
import { apiUpdateToken } from "services/AuthService";
import { setupNotificationSoundUnlock, prepareNotificationSound } from "utils/notificationSound";

let messagingInstance = null;
let lastRegisteredToken = null;

const getFirebaseApp = () => {
  if (getApps().length === 0) {
    return initializeApp(firebaseConfig);
  }
  return getApps()[0];
};

const getFirebaseMessaging = async () => {
  const supported = await isSupported();
  if (!supported) {
    console.warn("Firebase messaging is not supported in this browser.");
    return null;
  }

  if (!messagingInstance) {
    const app = getFirebaseApp();
    messagingInstance = getMessaging(app);
  }

  return messagingInstance;
};

const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser.");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
    );
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error("Failed to register Firebase service worker:", error);
    return null;
  }
};

export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  prepareNotificationSound();
  return Notification.requestPermission();
};

export const registerDeviceToken = async (authToken) => {
  if (!firebaseVapidKey) {
    console.warn("Firebase VAPID key is missing. Push notifications are disabled.");
    return null;
  }

  const permission = await requestNotificationPermission();
  if (permission !== "granted") {
    console.warn(`Notification permission not granted: ${permission}`);
    return null;
  }

  setupNotificationSoundUnlock();

  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return null;
  }

  const registration = await registerServiceWorker();
  if (!registration) {
    return null;
  }

  let deviceToken;
  try {
    deviceToken = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });
  } catch (error) {
    console.error("Failed to get Firebase device token:", error);
    return null;
  }

  if (!deviceToken) {
    console.warn("Firebase returned an empty device token.");
    return null;
  }

  if (lastRegisteredToken === deviceToken) {
    return deviceToken;
  }

  await apiUpdateToken(
    {
      deviceToken,
      deviceType: "web",
    },
    authToken,
  );

  lastRegisteredToken = deviceToken;
  return deviceToken;
};

export const subscribeToForegroundMessages = async (callback) => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, callback);
};
